import React, { useState, useEffect } from 'react';
import {
  Typography, Box, Card, CardContent, CardHeader,
  CircularProgress, Alert, AlertTitle,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Tooltip, Divider, Paper, Stack, Button,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  CreditCard as CreditCardIcon,
  Store as StoreIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import AdminLayout from '../../layouts/AdminLayout';
import { AdminService } from '../../services/adminService';

const PLAN_LABELS = {
  free: 'Free',
  trial: 'Trial',
  premium: 'Premium',
  premium_plus: 'Premium+',
};

const PLAN_COLORS = {
  free: 'default',
  trial: 'info',
  premium: 'primary',
  premium_plus: 'secondary',
};

const PLATFORM_ICONS = {
  web: <CreditCardIcon fontSize="small" />,
  all: <CreditCardIcon fontSize="small" />,
  ios: <StoreIcon fontSize="small" />,
  android: <StoreIcon fontSize="small" />,
};

function StatusChip({ status }) {
  if (status === 'ok')
    return <Chip icon={<CheckCircleIcon />} label="OK" color="success" size="small" />;
  if (status === 'warning')
    return <Chip icon={<WarningIcon />} label="Avertissement" color="warning" size="small" />;
  return <Chip icon={<ErrorIcon />} label="Erreur" color="error" size="small" />;
}

function CheckList({ checks }) {
  if (!checks || checks.length === 0)
    return <Typography variant="caption" color="text.secondary">Aucun problème</Typography>;
  return (
    <Stack spacing={0.5}>
      {checks.map((c, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {c.level === 'error'
            ? <ErrorIcon color="error" sx={{ fontSize: 14 }} />
            : <WarningIcon color="warning" sx={{ fontSize: 14 }} />}
          <Typography variant="caption">{c.msg}</Typography>
        </Box>
      ))}
    </Stack>
  );
}

function StripeDetails({ details }) {
  if (!details) return <Typography variant="caption" color="text.disabled">—</Typography>;
  if (!details.ok) return <Typography variant="caption" color="error">{details.error}</Typography>;
  return (
    <Stack spacing={0.3}>
      <Typography variant="caption">
        <b>{(details.unitAmount / 100).toFixed(2)} {details.currency?.toUpperCase()}</b>
        {' '}· {details.type}
        {details.recurring ? ` / ${details.recurring.interval}` : ''}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Produit : {details.productName || details.productId || '—'}
        {details.productActive === false && <span style={{ color: 'red' }}> (inactif)</span>}
      </Typography>
    </Stack>
  );
}

// ─── Matrice features × plans ─────────────────────────────────────────────────
function FeaturesMatrix({ features, plans }) {
  if (!features || features.length === 0) return null;

  const planList = plans || ['free', 'trial', 'premium', 'premium_plus'];

  const getCellValue = (feature, planType) => {
    if (!feature.planAssignments) return null;
    const assignments = Array.isArray(feature.planAssignments)
      ? feature.planAssignments
      : JSON.parse(feature.planAssignments);
    return assignments.find(a => a.planType === planType) || null;
  };

  const groupedByCategory = features.reduce((acc, f) => {
    const cat = f.category || 'autre';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(f);
    return acc;
  }, {});

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Feature</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Catégorie</TableCell>
            {planList.map(plan => (
              <TableCell key={plan} align="center" sx={{ fontWeight: 'bold' }}>
                <Chip label={PLAN_LABELS[plan] || plan} color={PLAN_COLORS[plan] || 'default'} size="small" />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(groupedByCategory).map(([category, catFeatures]) =>
            catFeatures.map((feature, idx) => (
              <TableRow
                key={feature.featureId}
                sx={{
                  backgroundColor: idx % 2 === 0 ? 'white' : 'grey.50',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                    {feature.featureId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{feature.name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{category}</Typography>
                </TableCell>
                {planList.map(plan => {
                  const assignment = getCellValue(feature, plan);
                  if (!assignment) {
                    return (
                      <TableCell key={plan} align="center">
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      </TableCell>
                    );
                  }
                  if (!assignment.isIncluded) {
                    return (
                      <TableCell key={plan} align="center">
                        <Tooltip title="Défini mais exclu">
                          <LockIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                        </Tooltip>
                      </TableCell>
                    );
                  }
                  if (assignment.limitValue != null) {
                    return (
                      <TableCell key={plan} align="center">
                        <Tooltip title={`Limite : ${assignment.limitValue}/${assignment.limitPeriod}`}>
                          <Chip
                            label={`${assignment.limitValue}/${assignment.limitPeriod}`}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        </Tooltip>
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell key={plan} align="center">
                      <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function ProductsAuditScreen() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchAudit = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await AdminService.getProductsAudit();
      setData(result);
    } catch (err) {
      setError(err.message || 'Impossible de charger l\'audit produits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAudit(); }, []);

  const { summary, products, features } = data || {};

  return (
    <AdminLayout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Audit produits & monétisation</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchAudit}
          disabled={loading}
        >
          Rafraîchir
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }}><AlertTitle>Erreur</AlertTitle>{error}</Alert>}

      {!loading && data && (
        <Stack spacing={3}>

          {/* ── Résumé ────────────────────────────────────────────────────── */}
          {!summary.stripeAvailable && (
            <Alert severity="warning">
              <AlertTitle>Clé Stripe non configurée</AlertTitle>
              La variable d'environnement <code>STRIPE_SECRET_KEY</code> est absente — la vérification Stripe est désactivée.
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Card sx={{ flex: 1, minWidth: 140 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" color="text.primary">{summary.total}</Typography>
                <Typography variant="body2" color="text.secondary">Produits total</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 140, border: summary.ok === summary.total ? '1px solid' : undefined, borderColor: 'success.main' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" color="success.main">{summary.ok}</Typography>
                <Typography variant="body2" color="text.secondary">OK</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 140, border: summary.warning > 0 ? '1px solid' : undefined, borderColor: 'warning.main' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" color={summary.warning > 0 ? 'warning.main' : 'text.disabled'}>
                  {summary.warning}
                </Typography>
                <Typography variant="body2" color="text.secondary">Avertissements</Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 140, border: summary.error > 0 ? '1px solid' : undefined, borderColor: 'error.main' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" color={summary.error > 0 ? 'error.main' : 'text.disabled'}>
                  {summary.error}
                </Typography>
                <Typography variant="body2" color="text.secondary">Erreurs</Typography>
              </CardContent>
            </Card>
          </Box>

          {/* ── Liste des problèmes ───────────────────────────────────────── */}
          {(summary.error > 0 || summary.warning > 0) && (
            <Card>
              <CardHeader
                title="Problèmes détectés"
                avatar={<ErrorIcon color={summary.error > 0 ? 'error' : 'warning'} />}
              />
              <CardContent sx={{ pt: 0 }}>
                <Stack spacing={1}>
                  {products
                    .filter(p => p.status !== 'ok')
                    .map(p => (
                      <Alert
                        key={p.productId}
                        severity={p.status === 'error' ? 'error' : 'warning'}
                        sx={{ alignItems: 'flex-start' }}
                      >
                        <AlertTitle>
                          <strong>{p.name}</strong>
                          <Typography component="span" variant="caption" sx={{ ml: 1, fontFamily: 'monospace' }}>
                            ({p.productId})
                          </Typography>
                        </AlertTitle>
                        {p.checks.map((c, i) => (
                          <Typography key={i} variant="body2">• {c.msg}</Typography>
                        ))}
                      </Alert>
                    ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* ── Tableau produits ──────────────────────────────────────────── */}
          <Card>
            <CardHeader title="Produits en base" subheader="Lecture seule — aucune modification possible depuis cette vue" />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>ID produit</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Nom</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Prix DB</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Durée</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Plateforme</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Stripe Price ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Stripe (données réelles)</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Store ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Actif</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map(p => (
                    <TableRow
                      key={p.id}
                      sx={{
                        backgroundColor:
                          p.status === 'error' ? 'rgba(244,67,54,0.05)' :
                          p.status === 'warning' ? 'rgba(255,152,0,0.05)' : 'white',
                        '&:hover': { backgroundColor: 'action.hover' },
                        verticalAlign: 'top',
                      }}
                    >
                      <TableCell sx={{ pt: 1.5 }}>
                        <Tooltip
                          title={p.checks.length > 0
                            ? p.checks.map(c => c.msg).join(' | ')
                            : 'Aucun problème'}
                          arrow
                        >
                          <span><StatusChip status={p.status} /></span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {p.productId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{p.name}</Typography>
                        {p.description && (
                          <Typography variant="caption" color="text.secondary">{p.description}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {Number(p.priceEur).toFixed(2)} €
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption">{p.durationDays}j</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={p.platform}>
                          <Chip
                            icon={PLATFORM_ICONS[p.platform]}
                            label={p.platform}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {p.stripePriceId
                          ? <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                              {p.stripePriceId}
                            </Typography>
                          : <Typography variant="caption" color="text.disabled">—</Typography>
                        }
                      </TableCell>
                      <TableCell>
                        <StripeDetails details={p.stripeDetails} />
                      </TableCell>
                      <TableCell>
                        {p.platformProductId
                          ? <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                              {p.platformProductId}
                            </Typography>
                          : <Typography variant="caption" color="text.disabled">—</Typography>
                        }
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={p.isActive ? 'Oui' : 'Non'}
                          color={p.isActive ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* ── Matrice features / plans ──────────────────────────────────── */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Matrice features / plans</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2, alignSelf: 'center' }}>
                {features?.length ?? 0} features · {summary.plans?.length ?? 0} plans
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0, pb: 2, px: 2 }}>
              <FeaturesMatrix features={features} plans={summary.plans} />
            </AccordionDetails>
          </Accordion>

        </Stack>
      )}
    </AdminLayout>
  );
}
